import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type Session, type Snapshot } from '../api';
import { Page } from '../components/Chrome';
import { Calendar } from '../components/Calendar';
import { startSession } from '../components/RoomPanel';
import { People, WorkspaceList, WorkspacePage, type WorkspaceRow } from './Workspaces';
import { T, ex, t, tenant } from '../tenant';

export function HostHome({ workspaceId }: { workspaceId?: string } = {}) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  useEffect(() => {
    api<Session>('/api/session').then(setSession, () => setSession(null));
  }, []);
  if (session === undefined) return <Page>{<div className="spinner" />}</Page>;
  if (!session) return <Login onDone={setSession} />;
  if (session.mustChangePassword) return <ChangePassword onDone={setSession} />;
  if (workspaceId) {
    return (
      <Page title={T.workspace} bar={<NavBar session={session} onSignOut={() => setSession(null)} />}>
        <div className="card wide">
          <WorkspacePage id={workspaceId} session={session} />
        </div>
      </Page>
    );
  }
  return <Dashboard session={session} onSignOut={() => setSession(null)} />;
}

function Login({ onDone }: { onDone: (s: Session) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      onDone(await api<Session>('/api/login', { body: { username: f.get('username'), password: f.get('password'), remember: f.get('remember') === 'on' } }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Page title="Sign in">
      <form className="card" onSubmit={submit}>
        <h1 className="h1">Sign in</h1>
        <p className="muted small" style={{ margin: 0 }}>
          For the host and {t.workspace} members. {T.guests} do not sign in; they join through the link they were sent.
        </p>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        <div>
          <label className="label" htmlFor="username">
            Username
          </label>
          <input id="username" name="username" className="field" autoComplete="username" required />
        </div>
        <div>
          <label className="label" htmlFor="password">
            Password
          </label>
          <input id="password" name="password" type="password" className="field" autoComplete="current-password" required />
        </div>
        <label className="row small">
          <input type="checkbox" name="remember" defaultChecked /> Keep me signed in on this device
        </label>
        <button className="btn primary block" disabled={busy}>
          Sign in
        </button>
      </form>
    </Page>
  );
}

function ChangePassword({ onDone }: { onDone: (s: Session) => void }) {
  const [error, setError] = useState<string | null>(null);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      onDone(await api<Session>('/api/password', { body: { currentPassword: f.get('current'), password: f.get('next') } }));
    } catch (err) {
      setError((err as Error).message);
    }
  }
  return (
    <Page title="Change password">
      <form className="card" onSubmit={submit}>
        <h1 className="h1">Choose your own password</h1>
        {error && <div className="error">{error}</div>}
        <input name="current" type="password" className="field" placeholder="Temporary password" autoComplete="current-password" required />
        <input name="next" type="password" className="field" placeholder="New password (10 or more characters)" minLength={10} autoComplete="new-password" required />
        <button className="btn primary block">Save</button>
      </form>
    </Page>
  );
}

interface OpenMeeting {
  id: string;
  title: string;
  created_at: string;
  workspace_title: string | null;
}

function NavBar({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  return (
    <>
      <a className="btn sm" href="/">
        {session.role === 'member' ? T.workspaces : T.meetings}
      </a>
      <span className="muted">{session.displayName}</span>
      <button
        className="btn sm"
        onClick={async () => {
          await api('/api/logout', { body: {} }).catch(() => {});
          onSignOut();
        }}
      >
        Sign out
      </button>
    </>
  );
}

type Tab = 'meetings' | 'workspaces' | 'calendar' | 'people';
const tabLabel: Record<Tab, string> = { meetings: T.meetings, workspaces: T.workspaces, calendar: 'Calendar', people: 'People' };

function CopyLink({ path }: { path: string }) {
  const url = `${location.origin}${path}`;
  const [copied, setCopied] = useState(false);
  return (
    <div className="linkbox">
      <input className="field" readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label="Guest link" />
      <button
        className="btn primary"
        onClick={async () => {
          await navigator.clipboard?.writeText(url).catch(() => {});
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? 'Copied' : 'Copy link'}
      </button>
    </div>
  );
}

function Dashboard({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const admin = session.role !== 'member';
  const initial = (new URLSearchParams(location.search).get('tab') as Tab | null) ?? (admin ? 'meetings' : 'workspaces');
  const [tab, setTab] = useState<Tab>(admin || initial === 'calendar' ? initial : 'workspaces');
  const tabs: Tab[] = admin ? ['meetings', 'workspaces', ...(tenant.features.calendar ? (['calendar'] as Tab[]) : []), 'people'] : ['workspaces', ...(tenant.features.calendar ? (['calendar'] as Tab[]) : [])];
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [meetings, setMeetings] = useState<OpenMeeting[]>([]);
  const [created, setCreated] = useState<{ id: string; joinPath: string; title: string } | null>(null);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const load = () => (admin ? api<{ meetings: OpenMeeting[] }>('/api/meetings').then((r) => setMeetings(r.meetings), (e: Error) => setError(e.message)) : Promise.resolve());
  useEffect(() => {
    void load();
    api<{ workspaces: WorkspaceRow[] }>('/api/workspaces').then((r) => setWorkspaces(r.workspaces), () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    setError(null);
    try {
      const r = await api<{ id: string; joinPath: string; title: string }>('/api/meetings', { body: { title: f.get('title'), waitingRoom: f.get('waitingRoom') === 'on', workspaceId: f.get('workspaceId') || null } });
      setCreated(r);
      form.reset();
      void load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function showLink(id: string) {
    try {
      const s = await api<Snapshot>(`/api/m/${id}/admin`);
      setLinks((l) => ({ ...l, [id]: `/m/${id}?k=${s.link}` }));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function end(id: string) {
    try {
      await api(`/api/m/${id}/end`, { body: {} });
      void load();
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 410)) setError((err as Error).message);
    }
  }

  return (
    <Page title={admin ? tenant.appName : T.workspaces} bar={<NavBar session={session} onSignOut={onSignOut} />}>
      <div className="card wide">
        {tabs.length > 1 && (
          <div className="tabs-row" role="tablist">
            {tabs.map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} className={`tab-btn ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)} data-testid={`tab-${t}`}>
                {tabLabel[t]}
              </button>
            ))}
          </div>
        )}
        {tab === 'workspaces' && <WorkspaceList admin={admin} />}
        {tab === 'people' && admin && <People />}
        {tab === 'calendar' && tenant.features.calendar && (
          <Calendar
            workspaces={admin ? workspaces : workspaces.filter((w) => w.can_upload)}
            canEdit={admin || workspaces.some((w) => w.can_upload)}
            onStartSession={admin && tenant.features.rooms ? (o) => startSession(o.workspaceId!).catch((e: Error) => setError(e.message)) : undefined}
          />
        )}
        {tab === 'calendar' && error && <div className="error">{error}</div>}
        {tab === 'meetings' && admin && (
          <>
        <h1 className="h1">Start a {t.meeting}</h1>
        {error && <div className="error">{error}</div>}
        <form className="list" onSubmit={create}>
          <div>
            <label className="label" htmlFor="title">
              Title
            </label>
            <input id="title" name="title" className="field" placeholder={`e.g. ${ex.meeting}`} maxLength={120} required />
          </div>
          <div>
            <label className="label" htmlFor="workspaceId">
              {T.workspace} (files for this {t.meeting})
            </label>
            <select id="workspaceId" name="workspaceId" className="field" defaultValue="">
              <option value="">None</option>
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.title} ({w.files} file{w.files === 1 ? '' : 's'})
                </option>
              ))}
            </select>
          </div>
          <label className="row small">
            <input type="checkbox" name="waitingRoom" defaultChecked /> Waiting room: I admit each {t.guest}
          </label>
          <button className="btn primary">Create {t.meeting}</button>
        </form>
        {created && (
          <div className="list" data-testid="created">
            <div className="h2">{created.title}</div>
            <CopyLink path={created.joinPath} />
            <div className="row">
              <a className="btn primary" href={`/m/${created.id}`}>
                Open {t.meeting}
              </a>
              <span className="muted small">The link stops working when you end the {t.meeting}, or after 30 minutes without activity.</span>
            </div>
          </div>
        )}
        <h2 className="h2" style={{ marginTop: 8 }}>
          Open {t.meetings}
        </h2>
        {meetings.length === 0 && <div className="muted small">None.</div>}
        <div className="list">
          {meetings.map((m) => (
            <div key={m.id} className="list-item">
              <div style={{ flex: 1, minWidth: 160 }}>
                <div>{m.title}</div>
                <div className="muted small">
                  Started {new Date(m.created_at).toLocaleString()}
                  {m.workspace_title && ` · workspace: ${m.workspace_title}`}
                </div>
              </div>
              <a className="btn sm" href={`/m/${m.id}`}>
                Open
              </a>
              <button className="btn sm" onClick={() => showLink(m.id)}>
                Link
              </button>
              <button className="btn sm danger" onClick={() => end(m.id)}>
                End
              </button>
              {links[m.id] && (
                <div style={{ flexBasis: '100%' }}>
                  <CopyLink path={links[m.id]!} />
                </div>
              )}
            </div>
          ))}
        </div>
          </>
        )}
      </div>
    </Page>
  );
}
